import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  BusinessNumberSearchValue,
  KybEntitySummary,
} from '@portal-workspace/grow-shared-library';
import { setupUntilDestroy } from '@portal-workspace/grow-ui-library';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Subscription } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { BusinessNumberSearchComponent, BusinessNumberSearchFn } from '../business-number-search-component/business-number-search.component';

/** The party kinds a corporate entity can be added as from the KYC/AML tab. */
export type AddCorporatePartyRole = 'trustee' | 'partner' | 'guarantor';

export interface AddApplicationPartyDialogData {
  /** ABN/ACN lookup for the organisation field. */
  businessNumberSearchFn: BusinessNumberSearchFn;
  /**
   * Entities already on the application that can own a trustee/partner (trusts and partnerships).
   * Empty when adding an application-level guarantor, which has no owning entity.
   */
  ownerEntities: KybEntitySummary[];
  /** Locks the role and hides the role selector when the caller already knows it (e.g. Add Guarantor). */
  fixedRole?: AddCorporatePartyRole;
  /** Pre-selects and hides the owner selector when the add is opened from a specific entity row. */
  fixedOwnerId?: number;
}

export interface AddApplicationPartyDialogResult {
  cancelled: boolean;
  valid: boolean;
  party?: {
    role: AddCorporatePartyRole;
    /** Present for trustee/partner; omitted for an application-level guarantor. */
    parentCommercialEntityId?: number;
    legalName: string;
    abn: string | null;
    acn: string | null;
    entityType: string | null;
  };
}

@UntilDestroy({ arrayName: 'subscriptions' })
@Component({
  selector: 'add-application-party-dialog',
  templateUrl: './add-application-party.dialog.html',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatButtonModule,
    FlexModule,
    BusinessNumberSearchComponent,
  ],
})
export class AddApplicationPartyDialog implements OnInit {
  subscriptions: Subscription[] = [];

  formControlOrganisation: FormControl<BusinessNumberSearchValue>;
  formControlRole: FormControl<AddCorporatePartyRole | null>;
  formControlOwner: FormControl<number | null>;
  formGroup: FormGroup<{
    organisation: FormControl<BusinessNumberSearchValue>,
    role: FormControl<AddCorporatePartyRole | null>,
    owner: FormControl<number | null>,
  }>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AddApplicationPartyDialogData,
    private matDialogRef: MatDialogRef<AddApplicationPartyDialog, AddApplicationPartyDialogResult>,
    private formBuilder: FormBuilder,
  ) {
    this.formControlOrganisation = formBuilder.control(null, [Validators.required]);
    this.formControlRole = formBuilder.control(data.fixedRole ?? null, [Validators.required]);
    this.formControlOwner = formBuilder.control(null);
    this.formGroup = formBuilder.group({
      organisation: this.formControlOrganisation,
      role: this.formControlRole,
      owner: this.formControlOwner,
    });
  }

  ngOnInit(): void {
    setupUntilDestroy(this);
    if (this.data.fixedOwnerId != null) {
      this.formControlOwner.setValue(this.data.fixedOwnerId);
    }
    // An owner is required for trustee/partner; a guarantor is application-level and has none.
    this.updateOwnerValidator();
    this.subscriptions.push(
      this.formControlRole.valueChanges.subscribe(() => this.updateOwnerValidator()),
    );
  }

  private updateOwnerValidator() {
    if (this.needsOwner()) {
      this.formControlOwner.setValidators([Validators.required]);
    } else {
      this.formControlOwner.clearValidators();
      this.formControlOwner.setValue(null);
    }
    this.formControlOwner.updateValueAndValidity();
  }

  needsOwner(): boolean {
    const role = this.formControlRole.value;
    return role === 'trustee' || role === 'partner';
  }

  /** Role-aware dialog title, e.g. "Add trustee" / "Add partner" / "Add guarantor". */
  title(): string {
    const role = this.data.fixedRole ?? this.formControlRole.value;
    switch (role) {
      case 'trustee': return 'Add trustee';
      case 'partner': return 'Add partner';
      case 'guarantor': return 'Add guarantor';
      default: return 'Add business entity';
    }
  }

  /** Display name of the pre-selected owning entity, when the add is opened from a specific row. */
  fixedOwnerName(): string | null {
    if (this.data.fixedOwnerId == null) {
      return null;
    }
    return this.data.ownerEntities.find(e => e.commercialEntityId === this.data.fixedOwnerId)?.entityName ?? null;
  }

  onCancel() {
    this.matDialogRef.close({ cancelled: true, valid: this.formGroup.valid });
  }

  onSubmit() {
    const org = this.formControlOrganisation.value;
    const role = this.formControlRole.value;
    if (!org || !role) {
      return;
    }
    const result: AddApplicationPartyDialogResult = {
      cancelled: false,
      valid: this.formGroup.valid,
      party: {
        role,
        parentCommercialEntityId: this.needsOwner() ? (this.formControlOwner.value ?? undefined) : undefined,
        legalName: org.name ?? org.displayName ?? '',
        abn: org.abn ?? null,
        acn: org.acn ?? null,
        entityType: null,
      },
    };
    this.matDialogRef.close(result);
  }

  disableSubmission(): boolean {
    return this.formGroup.invalid;
  }
}
